import { Box, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { DeliveriesFilters } from 'api/models/Deliveries';
import Page from 'components/Page';
import { PageHeader } from 'components/PageHeader';
import ReceiveDeliveryButton from 'components/ReceiveDeliveryButton';
import SearchInput from 'components/SearchInput';
import { useLanguage } from 'context/AppContext';
import { useActionItemsFilters } from './hooks/useActionItemsFilters';
import ActionItemsMenu from './components/ActionItemsMenu';
import theme from 'App.theme';
import { ActionItemsTotals, ActionItemType } from './types';
import useActionItems from './hooks/useActionItems';
import DeliveriesActionItem from './components/DeliveriesActionItem';
import useDeliveries from './hooks/useDeliveries';
import { format } from 'date-fns';
import { useSelectedActionItem } from './hooks/useSelectedActionItem';
import OrdersActionItem from './components/OrdersActionItem';
import { useIsTierZero } from 'hooks/useSubscriptionTier';
import { useCurrentUser } from 'api/hooks/useCurrentUser';
import { TenantType } from 'api/models/Users';
import routes from 'routes';
import { Navigate, useNavigate } from 'react-router';

// Helper function to get the formatted date string
const getFormattedDate = () => format(new Date(), 'd MMMM yyyy (E)');

const ActionItemsContent: FC = () => {
  const langAll = useLanguage()[0];
  const lang = langAll.ActionItemsPage;
  const [dateTodayString, setDateTodayString] = useState(getFormattedDate());

  // redirect for tier zero users
  const { data: user } = useCurrentUser();
  const isTierZero = useIsTierZero();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;

    const postLoginRedirect = sessionStorage.getItem('postLoginRedirect');
    if (!postLoginRedirect) return;
    sessionStorage.removeItem('postLoginRedirect');

    if (!isTierZero) return;

    navigate(routes.Home.makePath(), { replace: true });
  }, [isTierZero, navigate, user]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setDateTodayString(getFormattedDate());
    }, 60000); // 1 minute

    return () => clearInterval(intervalId);
  }, []);

  const [filters, setFilters] = useActionItemsFilters();

  const [selectedActionItem] = useSelectedActionItem();
  const actionItems = useActionItems();
  const actionItem = actionItems[selectedActionItem];

  // load the selected tab first for faster UX
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  // preload the data for all tabs
  const criticalItems = useDeliveries({
    filters: actionItems[ActionItemType.CriticalItems].filters,
    disabled: !initialLoadDone && selectedActionItem != ActionItemType.CriticalItems,
  });
  const deliveriesToBeReceived = useDeliveries({
    filters: actionItems[ActionItemType.DeliveriesToBeReceived].filters,
    disabled: !initialLoadDone && selectedActionItem != ActionItemType.DeliveriesToBeReceived,
  });
  const deliveriesToBeCheckedIn = useDeliveries({
    filters: actionItems[ActionItemType.DeliveriesToBeCheckedIn].filters,
    disabled: !initialLoadDone && selectedActionItem != ActionItemType.DeliveriesToBeCheckedIn,
  });
  const deliveriesToBeCheckedOut = useDeliveries({
    filters: actionItems[ActionItemType.DeliveriesToBeCheckedOut].filters,
    disabled: !initialLoadDone && selectedActionItem != ActionItemType.DeliveriesToBeCheckedOut,
  });

  const isInitialLoad =
    (selectedActionItem == ActionItemType.CriticalItems && criticalItems.isLoading) ||
    (selectedActionItem == ActionItemType.DeliveriesToBeReceived && deliveriesToBeReceived.isLoading) ||
    (selectedActionItem == ActionItemType.DeliveriesToBeCheckedIn && deliveriesToBeCheckedIn.isLoading) ||
    (selectedActionItem == ActionItemType.DeliveriesToBeCheckedOut && deliveriesToBeCheckedOut.isLoading);

  useEffect(() => {
    if (initialLoadDone || isInitialLoad) return;
    setInitialLoadDone(true);
  }, [initialLoadDone, isInitialLoad, selectedActionItem]);

  const recordTotals = useMemo<ActionItemsTotals>(
    () => ({
      [ActionItemType.CriticalItems]: {
        total: criticalItems.total,
        expressCount: criticalItems.expressCount,
      },
      [ActionItemType.BookingsToBeConfirmed]: {
        total: 0,
        expressCount: 0,
      },
      [ActionItemType.DeliveriesToBeReceived]: {
        total: deliveriesToBeReceived.total,
        expressCount: deliveriesToBeReceived.expressCount,
      },
      [ActionItemType.DeliveriesToBeCheckedIn]: {
        total: deliveriesToBeCheckedIn.total,
        expressCount: deliveriesToBeCheckedIn.expressCount,
      },
      [ActionItemType.DeliveriesToBeCheckedOut]: {
        total: deliveriesToBeCheckedOut.total,
        expressCount: deliveriesToBeCheckedOut.expressCount,
      },
    }),
    [
      criticalItems.expressCount,
      criticalItems.total,
      deliveriesToBeCheckedIn.expressCount,
      deliveriesToBeCheckedIn.total,
      deliveriesToBeCheckedOut.expressCount,
      deliveriesToBeCheckedOut.total,
      deliveriesToBeReceived.expressCount,
      deliveriesToBeReceived.total,
    ],
  );

  // quietly update the filters and invoke a data search
  const updateFilters = useCallback(
    (filters: DeliveriesFilters) => {
      setFilters(filters);
    },
    [setFilters],
  );

  const handleSearch = useCallback(
    (search: string) => {
      if (search !== filters.search) {
        updateFilters({ ...filters, search });
      }
    },
    [filters, updateFilters],
  );

  return user ? (
    <Page>
      <PageHeader
        title={
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.layout.spacing.tiny }}>
            <Box>{lang.title}</Box>
            <Typography variant="subtitle1" color="textSecondary">
              {lang.dateToday}: {dateTodayString}
            </Typography>
          </Box>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: theme.layout.spacing.medium }}>
          <SearchInput
            value={filters.search}
            onChange={handleSearch}
            placeholder={langAll.SearchInput.searchOrScanPlaceholder}
            width="460px"
          />
          <ReceiveDeliveryButton />
        </Box>
      </PageHeader>

      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <ActionItemsMenu recordTotals={recordTotals} />

        {actionItem.dataType == 'orders' ? (
          <OrdersActionItem actionItem={actionItem} />
        ) : (
          <DeliveriesActionItem actionItem={actionItem} />
        )}
      </Box>
    </Page>
  ) : null;
};

// Smartways operations users only have the All Orders page, so redirect before any hospital queries fire
const ActionItemsPage: FC = () => {
  const { data: user } = useCurrentUser();

  if (!user) return null;
  if (user.tenant.type == TenantType.ADMIN_OPERATIONS) return <Navigate to={routes.AllOrders.makePath()} replace />;

  return <ActionItemsContent />;
};

export default ActionItemsPage;
